import React from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { Seo, orgJsonLd, websiteJsonLd, faqJsonLd } from "../lib/seo";
import { Container, Section, Button, SectionHead, Eyebrow } from "../components/ui";
import { ConversationTranscript, StatBand, TestimonialWall, RatingHistogram, PricingTable, FaqAccordion, DataTable } from "../components/marketing";
import { LogoStrip } from "../components/chrome/LogoStrip";
import { useReveal } from "../lib/useReveal";

const CAPS = [
  ["It sells", "Qualifies leads, handles objections, builds trust and closes. From ₱1,500 products to ₱2.5M services.", "₱1.5k–₱2.5M"],
  ["It books", "Spots the buying signal and books the appointment inside the chat. No links, no forms, no drop-off.", "In-chat"],
  ["It understands everything", "Voice notes, photos, video and PDFs — not just text.", "Multimodal"],
  ["It remembers", "Every prior conversation, preference and follow-up. Up to a 100k-token memory window per contact.", "100k tokens"],
  ["It gets smarter", "One click. The AI reads your top-performing conversations and rewrites its own instructions.", "One-click"],
  ["It runs campaigns", "Outbound sequences, automatic follow-ups, and comment-to-DM on Instagram and Facebook.", "Campaigns"],
  ["It works everywhere", "Seven live channels, one inbox, one set of instructions.", "7 channels"],
  ["It connects to your stack", "Zapier, Make, n8n, GoHighLevel, Intercom, Tidio, Zendesk — plus webhooks, a REST API and an MCP server.", "API + MCP"],
];

const STEPS = [
  ["Paste your website", "The AI reads your products, pricing and positioning and drafts its own instructions."],
  ["Pick your channels", "Connect WhatsApp, Instagram, Messenger and more in a few clicks."],
  ["Test it in the playground", "Push back on price, send a voice note, send a photo. Tune the tone."],
  ["Turn it on", "Go live. It answers in seconds, at 3am, in your customer's language."],
];

export default function Home() {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => get("/stats") });
  const { data: testimonials } = useQuery({ queryKey: ["home-testimonials"], queryFn: () => get("/testimonials", { home: true }) });
  const { data: summary } = useQuery({ queryKey: ["reviews-summary"], queryFn: () => get("/reviews-summary") });
  const { data: benchmark } = useQuery({ queryKey: ["benchmark"], queryFn: () => get("/benchmark") });
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: () => get("/plans") });
  const { data: channels } = useQuery({ queryKey: ["channels"], queryFn: () => get("/channels-directory") });
  const { data: faqData } = useQuery({ queryKey: ["faqs"], queryFn: () => get("/faqs") });
  const faqs = (faqData?.faqs || []).slice(0, 8);
  useReveal(testimonials);

  return (
    <>
      <Seo title="The AI sales agent for your inbox" path="/"
        description="BloomCONNECT is an AI sales agent that qualifies leads, handles objections, understands voice notes and photos, and books appointments across seven channels."
        jsonLd={[orgJsonLd(), websiteJsonLd(), faqJsonLd(faqs)]} />

      {/* 01 Hero */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-12 items-start">
          <div className="lg:col-span-7">
            <Eyebrow>AI Sales Agent · 7 Channels</Eyebrow>
            <h1 className="mt-5 text-4xl md:text-6xl tracking-tight text-ink-950 max-w-[15ch]">From ‘just browsing’ to a booked call. Without a human touching the thread.</h1>
            <p className="mt-6 text-lg text-ink-600 max-w-prose">BloomCONNECT is an AI sales agent that lives in your inbox. It qualifies leads, handles objections, understands voice notes and photos, and books the appointment — inside WhatsApp, Instagram, Messenger, Telegram, Email, web chat and SMS. It answers in seconds, at 3am, in your customer's language.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/pricing/" variant="primary" size="lg" data-testid="home-see-pricing-btn">See Pricing</Button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <ConversationTranscript channel="WhatsApp"
              messages={[
                { from: "them", text: "hi, saw your reel on the aircon cleaning. how much for a 2-bedroom?", time: "23:14" },
                { from: "agent", text: "Evening! For a 2-bedroom split-type it's ₱1,800 for up to two units. Are both units split-type, or is one a window unit?", time: "23:14" },
                { from: "them", text: "both split", time: "23:15" },
                { from: "agent", text: "Perfect. I have Thursday 9am or Saturday 1pm this week. Which suits you?", time: "23:15" },
                { from: "them", text: "saturday", time: "23:16" },
                { from: "agent", text: "Booked for Saturday 1pm. I'll text a reminder the day before. Anything else you'd like cleaned while we're there?", time: "23:16" },
              ]} />
          </div>
        </div>
      </Section>
      <LogoStrip />

      {/* 02 Stat band */}
      <Section>
        <SectionHead n="01" label="By the numbers" title="What the agent is doing right now." />
        <div className="mt-8">{stats && <StatBand stats={stats} />}</div>
      </Section>

      {/* 03 It isn't a chatbot */}
      <Section sunk>
        <SectionHead n="02" label="What it is" title="It isn't a chatbot. It's a sales agent." intro="Eight things a decision tree cannot do." />
        <dl className="mt-10 divide-y divide-line border-y border-line">
          {CAPS.map(([name, copy, tag]) => (
            <div key={name} className="grid md:grid-cols-12 gap-2 md:gap-6 py-5 reveal-on-scroll">
              <dt className="md:col-span-3 text-base text-ink-950">{name}</dt>
              <dd className="md:col-span-7 text-ink-600">{copy}</dd>
              <dd className="md:col-span-2 md:text-right font-mono text-2xs uppercase tracking-eyebrow text-brand-700 self-center">{tag}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* 04 Live in 15 minutes */}
      <Section id="how">
        <SectionHead n="03" label="How it works" title="Live in 15 minutes." />
        <ol className="mt-10 grid gap-8 md:grid-cols-4 border-t border-line pt-8">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="reveal-on-scroll">
              <div className="font-mono text-2xs text-brand-700">0{i + 1}</div>
              <h3 className="mt-2 text-lg text-ink-950">{t}</h3>
              <p className="mt-2 text-sm text-ink-600">{d}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* 05 Proof conversation */}
      <Section sunk>
        <SectionHead n="04" label="Proof" title="‘Out of budget’ on day one. Deposit paid on day 14." intro="A real qualifying exchange, start to close. Zero human messages in the thread." />
        <div className="mt-10">
          <ConversationTranscript channel="Instagram DM"
            caption="Eight months on, the relationship is still live — and it started with a ‘no’."
            messages={[
              { from: "them", text: "love the work but honestly it's way out of my budget right now 😅", time: "Mon 10:02" },
              { from: "agent", text: "Completely fair — most people feel that at first. Can I ask what outcome you're actually after? If it's the wrong fit I'll tell you straight.", time: "Mon 10:03" },
              { from: "them", text: "i want to stop losing leads at night when i'm asleep", time: "Mon 10:05" },
              { from: "agent", text: "That's exactly the problem we solve. One recovered deal usually covers a few months of this. Want me to send a 2-minute breakdown of the maths for your numbers?", time: "Mon 10:05" },
              { from: "them", text: "ok send it", time: "Mon 10:06" },
              { from: "agent", text: "Sent. No rush — I'll check back Thursday.", time: "Mon 10:07" },
              { from: "them", text: "been thinking about it. can we start small?", time: "Fri 16:20" },
              { from: "agent", text: "Yes. Growth gets every channel live today. I can reserve onboarding for Monday and take a deposit to hold it — shall I?", time: "Fri 16:21" },
              { from: "them", text: "deposit sent ✅", time: "Fri 16:44" },
            ]}
            annotations={[
              "Opens on price. The agent re-frames on outcome, not discount.",
              "Offers value before asking for anything.",
              "Follows up on a schedule, then closes on a small first step.",
            ]} />
        </div>
      </Section>

      {/* 06 Channels */}
      <Section>
        <SectionHead n="05" label="Channels" title="Seven live channels. One inbox." />
        <div className="mt-8">
          {channels && <DataTable caption="Channels and availability"
            head={["Channel", "How it connects", "What the AI can do there", "Availability"]}
            rows={channels.map((c) => [c.name, c.connect, c.does, c.availability])} />}
        </div>
      </Section>

      {/* 07 Testimonials */}
      <Section sunk>
        <SectionHead n="06" label="Reviews" title="Teams that sell in the DMs, on the record." />
        <div className="mt-8">{summary && <RatingHistogram summary={summary} />}</div>
        <div className="mt-10">{testimonials && <TestimonialWall items={testimonials} />}</div>
        <div className="mt-8"><Button to="/reviews/" variant="secondary" size="md">Read all reviews</Button></div>
      </Section>

      {/* 08 Benchmark */}
      <Section>
        <SectionHead n="07" label="Benchmark" title="We benchmarked 13 AI models. Ours wins on value." intro="Every AI action costs the same flat 0.25 credits, versus 4–20× spikes on metered models. Same rubric for all thirteen." />
        <div className="mt-8">
          {benchmark && <DataTable caption="Model benchmark"
            head={["Model", "Reliability", "Cost per action"]}
            rows={benchmark.map((m) => [m.model, m.reliability, m.cost_per_action])} />}
        </div>
        <div className="mt-6"><Button to="/best/best-ai-model-for-sales-chatbots-2026/" variant="ghost" size="md">See the full methodology →</Button></div>
      </Section>

      {/* 09 Pricing preview */}
      <Section sunk>
        <SectionHead n="08" label="Pricing" title="Simple, flat pricing in pesos." intro="Prices in Philippine pesos. USD shown for reference. Accept Credit/Debit cards via PayPal." />
        <div className="mt-10">{plans && <PricingTable plans={plans} billing="monthly" />}</div>
        <div className="mt-8"><Button to="/pricing/" variant="secondary" size="md">Compare every plan</Button></div>
      </Section>

      {/* 10 FAQ */}
      <Section>
        <SectionHead n="09" label="FAQ" title="Questions buyers actually ask." />
        <div className="mt-8 max-w-3xl">{faqs.length > 0 && <FaqAccordion items={faqs} />}</div>
        <div className="mt-8"><Button to="/faq/" variant="ghost" size="md">All questions →</Button></div>
      </Section>
    </>
  );
}
